<template>
  <div class="stat-card" :class="[`stat-card--${accentName}`]">
    <div class="stat-card__glow"></div>
    <div class="stat-card__inner">
      <div class="stat-card__top">
        <div class="stat-card__title-wrap">
          <span class="stat-card__title">{{ formattedTitle }}</span>
        </div>
        <div class="stat-card__icon-box">
          <i :class="resolvedIconClass"></i>
        </div>
      </div>

      <div class="stat-card__bottom">
        <div class="stat-card__value-group">
          <span class="stat-card__value">{{ formattedValue }}</span>
          <span v-if="badgeText" class="stat-card__badge">
            {{ badgeText }}
          </span>
        </div>
        <div v-if="subtext" class="stat-card__subtext">
          {{ subtext }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MiniStatisticsCard",
  props: {
    iconClass: {
      type: String,
      default: "bi bi-activity",
    },
    accent: {
      type: String,
      default: "indigo", // amber, indigo, emerald, cyan, rose, purple
    },
    title: {
      type: [Object, String],
      default: "",
    },
    value: {
      type: [Object, String, Number],
      required: true,
    },
    badge: {
      type: String,
      default: "",
    },
    subtext: {
      type: String,
      default: "",
    },
    percentage: {
      type: [Object, String, Number],
      default: null,
    },
    classContent: {
      type: String,
      default: "",
    },
  },
  computed: {
    formattedTitle() {
      if (!this.title) return "";
      return typeof this.title === "object" ? this.title.text : this.title;
    },
    formattedValue() {
      if (this.value === null || this.value === undefined) return "0";
      return typeof this.value === "object" ? this.value.text : this.value;
    },
    badgeText() {
      if (this.badge) return this.badge;
      if (this.percentage) {
        if (typeof this.percentage === "object") return this.percentage.value || "";
        return `${this.percentage}`;
      }
      return "";
    },
    accentName() {
      if (this.accent) return this.accent;
      return "indigo";
    },
    resolvedIconClass() {
      if (this.iconClass && !this.iconClass.includes("fa-spinner")) {
        return this.iconClass;
      }
      const map = {
        amber: "bi bi-file-earmark-excel",
        indigo: "bi bi-pen-fill",
        emerald: "bi bi-chat-square-text-fill",
        cyan: "bi bi-clock-history",
      };
      return map[this.accent] || "bi bi-check2-circle";
    },
  },
};
</script>

<style scoped lang="scss">
.stat-card {
  --card-accent: #6366f1;
  --card-accent-rgb: 99, 102, 241;
  --card-accent-glow: rgba(99, 102, 241, 0.2);

  position: relative;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  min-height: 68px;
  background: var(--app-surface-elevated, #1c232b);
  border: 1px solid var(--app-border, rgba(255, 255, 255, 0.08));
  box-shadow: 0 4px 14px -2px rgba(0, 0, 0, 0.15);
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(var(--card-accent-rgb), 0.45);
    box-shadow: 0 8px 20px -4px var(--card-accent-glow), 0 2px 8px rgba(0, 0, 0, 0.1);

    .stat-card__icon-box {
      transform: scale(1.08) rotate(3deg);
      box-shadow: 0 4px 12px rgba(var(--card-accent-rgb), 0.35);
    }
  }

  /* Themes */
  &--amber,
  &--warning {
    --card-accent: #f59e0b;
    --card-accent-rgb: 245, 158, 11;
    --card-accent-glow: rgba(245, 158, 11, 0.25);
  }

  &--indigo,
  &--primary {
    --card-accent: #6366f1;
    --card-accent-rgb: 99, 102, 241;
    --card-accent-glow: rgba(99, 102, 241, 0.25);
  }

  &--emerald,
  &--success {
    --card-accent: #10b981;
    --card-accent-rgb: 16, 185, 129;
    --card-accent-glow: rgba(16, 185, 129, 0.25);
  }

  &--cyan,
  &--info {
    --card-accent: #0ea5e9;
    --card-accent-rgb: 14, 165, 233;
    --card-accent-glow: rgba(14, 165, 233, 0.25);
  }

  &--rose,
  &--danger {
    --card-accent: #f43f5e;
    --card-accent-rgb: 244, 63, 94;
    --card-accent-glow: rgba(244, 63, 94, 0.25);
  }

  &--purple {
    --card-accent: #a855f7;
    --card-accent-rgb: 168, 85, 247;
    --card-accent-glow: rgba(168, 85, 247, 0.25);
  }
}

.stat-card__glow {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: var(--card-accent);
  opacity: 0.15;
  filter: blur(22px);
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.stat-card:hover .stat-card__glow {
  opacity: 0.28;
}

.stat-card__inner {
  position: relative;
  z-index: 1;
  padding: 8px 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
}

.stat-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.stat-card__title-wrap {
  flex: 1;
  min-width: 0;
}

.stat-card__title {
  display: block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--app-text, #94a3b8);
  line-height: 1.25;
  word-break: break-word;
}

.stat-card__icon-box {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  background: rgba(var(--card-accent-rgb), 0.14);
  color: var(--card-accent);
  border: 1px solid rgba(var(--card-accent-rgb), 0.28);
  transition: all 0.25s ease;
}

.stat-card__bottom {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.stat-card__value-group {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
}

.stat-card__value {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  color: var(--app-heading, #f1f5f9);
  letter-spacing: -0.4px;
  font-variant-numeric: tabular-nums;
}

.stat-card__badge {
  font-size: 9.5px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 5px;
  background: rgba(var(--card-accent-rgb), 0.14);
  color: var(--card-accent);
  border: 1px solid rgba(var(--card-accent-rgb), 0.22);
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.stat-card__subtext {
  font-size: 10px;
  color: var(--app-text, #94a3b8);
  opacity: 0.85;
}

/* Light mode adjustments */
html[data-theme="light"] .stat-card {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .stat-card__title {
    color: #64748b;
  }

  .stat-card__value {
    color: #0f172a;
  }
}
</style>
